/**
 * Team setup page component
 * Shown to new users who don't have any teams yet
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/context/AuthContext';
import { useCreateTeam } from '@/hooks/useTeams';

export function TeamSetup() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const createTeam = useCreateTeam();
  const [teamName, setTeamName] = React.useState('');
  const [teamNameError, setTeamNameError] = React.useState('');

  const validateTeamName = (value: string): boolean => {
    if (!value) {
      setTeamNameError('Team name is required');
      return false;
    }
    if (value.length < 2) {
      setTeamNameError('Team name must be at least 2 characters');
      return false;
    }
    if (value.length > 50) {
      setTeamNameError('Team name must be less than 50 characters');
      return false;
    }
    setTeamNameError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateTeamName(teamName)) {
      return;
    }

    try {
      await createTeam.mutateAsync({ name: teamName });
      await checkAuth();
      // Navigate to dashboard on success
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Team creation error:', err);
      // Error is already set by the mutation
    }
  };

  return (
    <AuthLayout
      title="Create your team"
      subtitle="Let's get started by creating your first team workspace"
      footer="Need help? Contact support@adverdict.com"
    >
      <div className="rounded-lg border border-border-subtle bg-surface p-8 shadow-md">
          {/* Error Alert */}
          {createTeam.error && (
            <div
              className="mb-6 rounded-md border border-danger/20 bg-danger-soft px-4 py-3"
              role="alert"
              aria-live="polite"
            >
              <p className="text-sm text-danger">
                {createTeam.error instanceof Error
                  ? createTeam.error.message
                  : 'Failed to create team. Please try again.'}
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Team Name Input */}
            <Input
              id="teamName"
              type="text"
              label="Team name"
              placeholder="Acme Inc, Marketing Team, etc."
              value={teamName}
              onChange={(e) => {
                setTeamName(e.target.value);
                if (teamNameError) validateTeamName(e.target.value);
              }}
              onBlur={() => validateTeamName(teamName)}
              error={!!teamNameError}
              errorMessage={teamNameError}
              helperText="This will be the name of your workspace"
              disabled={createTeam.isPending}
              autoComplete="organization"
              autoFocus
              required
            />

            {/* Info Box */}
            <div className="rounded-md border border-info/20 bg-info-soft px-4 py-3">
              <p className="text-sm text-info">
                You can invite team members and manage settings after creating
                your team.
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={createTeam.isPending}
              disabled={createTeam.isPending || !teamName}
              className="w-full"
            >
              {createTeam.isPending ? 'Creating team...' : 'Get started'}
            </Button>
          </form>

          {/* Features List */}
          <div className="mt-8 space-y-3 border-t border-border-subtle pt-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted">
              What&apos;s included
            </p>
            <ul className="space-y-2" role="list">
              {[
                'Upload and analyze video creatives',
                'Get AI-powered Scale/Fix/Kill decisions',
                'Track creative performance metrics',
                'Generate weekly decision memos',
              ].map((feature, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 text-sm text-secondary"
                >
                  <div className="mt-0.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
      </div>
    </AuthLayout>
  );
}
